// ============================================================
// Timezone-safe date/time helpers built on the Temporal API
// (via the @js-temporal/polyfill, since no shipping browser in
// our browserslist target has native Temporal support yet).
//
// Used by Attendance Management for Check-In / Check-Out so that:
//  - times typed by the user (local wall-clock) are sent to the API as
//    UTC, formatted as "YYYY-MM-DD HH:mm" (the /attendance endpoint's
//    validator rejects seconds and ISO strings with a "T"/"Z")
//  - UTC timestamps returned by the API (which may include seconds) are
//    shown to the user in their own local time, correctly handling DST,
//    with the timezone auto-detected from the browser (never hardcoded).
// ============================================================

import { Temporal } from '@js-temporal/polyfill';
import dayjs from 'dayjs';

// Auto-detects the browser's IANA timezone (e.g. "Asia/Karachi").
// Never hardcode a fixed offset here — offsets don't account for DST.
export function getLocalTimeZone() {
    try {
        return Temporal.Now.timeZoneId();
    } catch (err) {
        // Extremely defensive fallback; Temporal.Now.timeZoneId() is not
        // expected to throw in supported environments.
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    }
}

// Accepts a dayjs instance or a 'YYYY-MM-DD HH:mm[:ss]' / ISO-like string
// representing LOCAL wall-clock time, and returns a Temporal.PlainDateTime.
function toPlainDateTime(localDateTime) {
    if (localDateTime && typeof localDateTime.format === 'function') {
        // dayjs instance
        return Temporal.PlainDateTime.from(localDateTime.format('YYYY-MM-DDTHH:mm:ss'));
    }
    if (typeof localDateTime === 'string') {
        let normalized = localDateTime.trim().replace(' ', 'T');
        if (normalized.length === 16) normalized += ':00'; // 'YYYY-MM-DDTHH:mm' -> add seconds
        return Temporal.PlainDateTime.from(normalized);
    }
    throw new TypeError('Unsupported localDateTime input');
}

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Converts a local wall-clock date/time into its UTC equivalent, formatted
 * as "YYYY-MM-DD HH:mm" — the exact format the /attendance endpoint's
 * validator requires ("The checkIn must be in HH:MM or YYYY-MM-DD HH:MM
 * format."). It rejects seconds and full ISO-8601 strings with a "T"/"Z".
 *
 * @param {import('dayjs').Dayjs|string} localDateTime - local time, as a
 *   dayjs instance or a 'YYYY-MM-DD HH:mm[:ss]' string.
 * @returns {string|null} 'YYYY-MM-DD HH:mm' in UTC, or null if the input is missing/invalid.
 */
export function localToUtc(localDateTime) {
    if (!localDateTime) return null;
    try {
        const plainDateTime = toPlainDateTime(localDateTime);
        const zoned = plainDateTime.toZonedDateTime(getLocalTimeZone());
        const utcDateTime = zoned.toInstant().toZonedDateTimeISO('UTC');
        return `${utcDateTime.year}-${pad2(utcDateTime.month)}-${pad2(utcDateTime.day)} ${pad2(utcDateTime.hour)}:${pad2(utcDateTime.minute)}`;
    } catch (err) {
        console.error('[dateTimeTemporal] localToUtc failed for value:', localDateTime, err);
        return null;
    }
}

/**
 * Converts a UTC timestamp from the API into the user's local wall-clock
 * time, returned as a dayjs instance so it can be fed directly into antd's
 * DatePicker/TimePicker `value`/`defaultValue` props.
 *
 * @param {string} utcDateTime - e.g. "2026-08-04T09:30:00Z" (a bare
 *   "YYYY-MM-DD HH:mm:ss" without an offset is also accepted and treated as UTC).
 * @returns {import('dayjs').Dayjs|null}
 */
export function utcToLocal(utcDateTime) {
    if (!utcDateTime) return null;
    try {
        let isoStr = String(utcDateTime).trim().replace(' ', 'T');
        if (!/[Zz]|[+-]\d{2}:\d{2}$/.test(isoStr)) isoStr += 'Z';
        const instant = Temporal.Instant.from(isoStr);
        const zoned = instant.toZonedDateTimeISO(getLocalTimeZone());
        return dayjs(zoned.epochMilliseconds);
    } catch (err) {
        console.error('[dateTimeTemporal] utcToLocal failed for value:', utcDateTime, err);
        return null;
    }
}

/**
 * Formats a UTC timestamp for display in the user's local timezone.
 * @param {string} utcDateTime
 * @param {string} displayFormat - dayjs format string, e.g. 'hh:mm A' or 'HH:mm'
 * @returns {string} formatted local time, or '-' if unavailable/invalid.
 */
export function formatUtcToLocalDisplay(utcDateTime, displayFormat = 'hh:mm A') {
    const local = utcToLocal(utcDateTime);
    return local && local.isValid() ? local.format(displayFormat) : '-';
}

/**
 * Number of hours between two UTC timestamps (checkIn/checkOut), rounded
 * to 2 decimals. Timezone-agnostic by nature (both are absolute instants),
 * but parsed via Temporal for consistency with the rest of this module.
 * @param {string} utcStart
 * @param {string} utcEnd
 * @returns {number} 0 if either input is missing/invalid.
 */
export function utcDiffInHours(utcStart, utcEnd) {
    if (!utcStart || !utcEnd) return 0;
    try {
        const normalize = (v) => {
            let s = String(v).trim().replace(' ', 'T');
            if (!/[Zz]|[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
            return s;
        };
        const start = Temporal.Instant.from(normalize(utcStart));
        const end = Temporal.Instant.from(normalize(utcEnd));
        const diffMinutes = end.since(start).total({ unit: 'minute' });
        return Number((diffMinutes / 60).toFixed(2));
    } catch (err) {
        console.error('[dateTimeTemporal] utcDiffInHours failed:', { utcStart, utcEnd }, err);
        return 0;
    }
}
